import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Box, Button, ButtonBase, Card, CardContent, Chip, CircularProgress, Divider, Grid,
  MenuItem, Paper, TextField, Typography, Tabs, Tab, useMediaQuery, useTheme
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import PersonIcon from "@material-ui/icons/Person";
import QueueIcon from "@material-ui/icons/Queue";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import Users from "../Users";
import Supervisors from "../Supervisors";
import toastError from "../../errors/toastError";

const statuses = [
  { value: "all", label: "Todas" },
  { value: "open", label: "Em atendimento" },
  { value: "pending", label: "Aguardando" },
  { value: "closed", label: "Encerradas" },
  { value: "group", label: "Grupos" },
  { value: "chatbot", label: "Chatbot" }
];

function ReadOnlyMessage({ message }) {
  const mediaType = String(message.mediaType || "").toLowerCase();
  const url = typeof message.mediaUrl === "string" && /^https?:\/\//i.test(message.mediaUrl) ? message.mediaUrl : null;
  if (message.isDeleted) return <Typography variant="body2" color="textSecondary">Mensagem removida</Typography>;
  return <>
    {message.isPrivate && <Typography variant="caption" color="textSecondary">Nota interna</Typography>}
    <Typography variant="body2">{message.body || (mediaType ? `[${mediaType}]` : "")}</Typography>
    {url && (mediaType.includes("audio") || mediaType === "ptt") ? <audio controls preload="none" src={url} style={{ width: "100%", maxWidth: 340 }} />
      : url && mediaType.includes("image") ? <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="Imagem da conversa" loading="lazy" style={{ maxWidth: "100%", maxHeight: 260 }} /></a>
      : url && mediaType.includes("video") ? <video controls preload="none" src={url} style={{ maxWidth: "100%", maxHeight: 260 }} />
      : url ? <a href={url} target="_blank" rel="noopener noreferrer">Abrir anexo</a> : null}
  </>;
}

const Management = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [directoryTab, setDirectoryTab] = useState("managers");
  const isManager = user?.profile === "manager";
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [tickets, setTickets] = useState([]);
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketsHaveMore, setTicketsHaveMore] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const ticketRequestRef = useRef(null);
  const messageRequestRef = useRef(null);
  const ticketRequestIdRef = useRef(0);
  const messageRequestIdRef = useRef(0);
  const selectedTicketRef = useRef(null);
  const loadingMessagePagesRef = useRef(new Set());
  const messagePageRef = useRef(1);

  const selectTicket = ticket => {
    if (messageRequestRef.current) messageRequestRef.current.cancel("Conversation changed.");
    loadingMessagePagesRef.current.clear();
    messagePageRef.current = 1;
    setMessages([]);
    setPage(1);
    setHasMore(false);
    selectedTicketRef.current = ticket;
    setSelectedTicket(ticket);
    loadMessages(ticket);
  };

  const metadataChip = (label, value, Icon, tone = "primary") => {
    const palette = theme.palette[tone];
    return <Chip
      size="small"
      color={tone}
      icon={<Icon fontSize="small" />}
      label={<span style={{ display: "flex", flexWrap: "wrap", columnGap: 4, whiteSpace: "normal", overflowWrap: "anywhere" }}><strong>{label}</strong><span>{value || "—"}</span></span>}
      style={{ height: "auto", minHeight: 26, maxWidth: "100%", alignItems: "flex-start", backgroundColor: palette.dark, color: palette.contrastText }}
    />;
  };

  const statusLabel = value => statuses.find(option => option.value === value)?.label || value || "Situação indisponível";
  const ticketGroups = [
    ...["pending", "open"].map(value => ({
      value,
      label: statusLabel(value),
      tickets: tickets.filter(ticket => ticket.status === value)
    })),
    ...statuses.filter(option => !["all", "pending", "open"].includes(option.value)).map(option => ({
      value: option.value,
      label: option.label,
      tickets: tickets.filter(ticket => ticket.status === option.value)
    })),
    {
      value: "other",
      label: "Outras conversas",
      tickets: tickets.filter(ticket => !statuses.some(option => option.value !== "all" && option.value === ticket.status))
    }
  ].filter(group => group.tickets.length > 0);

  useEffect(() => {
    selectedTicketRef.current = selectedTicket;
  }, [selectedTicket]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadTickets = useCallback(async (requestedPage = 1, append = false) => {
    if (!isManager) return;
    if (ticketRequestRef.current) ticketRequestRef.current.cancel("A newer ticket-list request replaced this one.");
    const source = axios.CancelToken.source();
    ticketRequestRef.current = source;
    const requestId = ++ticketRequestIdRef.current;
    setLoading(true);
    try {
      const { data } = await api.get("/management/tickets", { params: { page: requestedPage, status, search }, cancelToken: source.token });
      if (requestId !== ticketRequestIdRef.current) return;
      setTickets(previous => {
        const incoming = data.tickets || [];
        if (!append) return incoming;
        const byId = new Map(previous.map(ticket => [ticket.id, ticket]));
        incoming.forEach(ticket => byId.set(ticket.id, ticket));
        return Array.from(byId.values());
      });
      setTicketsHaveMore(Boolean(data.hasMore));
      setTicketPage(requestedPage);
    } catch (error) {
      if (!axios.isCancel(error) && requestId === ticketRequestIdRef.current) toastError(error);
    } finally {
      if (requestId === ticketRequestIdRef.current) {
        ticketRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [isManager, status, search]);

  const loadMessages = useCallback(async (ticket, messagePage = 1) => {
    if (!ticket?.id) return;
    if (loadingMessagePagesRef.current.has(messagePage)) return;
    if (messageRequestRef.current) messageRequestRef.current.cancel("A newer message request replaced this one.");
    const source = axios.CancelToken.source();
    messageRequestRef.current = source;
    const requestId = ++messageRequestIdRef.current;
    loadingMessagePagesRef.current.add(messagePage);
    setMessagesLoading(true);
    try {
      const { data } = await api.get(`/management/tickets/${ticket.id}/messages`, { params: { page: messagePage }, cancelToken: source.token });
      if (requestId !== messageRequestIdRef.current || selectedTicketRef.current?.id !== ticket.id) return;
      setSelectedTicket(data.ticket);
      setMessages(previous => {
        if (messagePage === 1) return data.messages || [];
        const byId = new Map(previous.map(message => [message.id, message]));
        (data.messages || []).forEach(message => byId.set(message.id, message));
        return Array.from(byId.values()).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });
      messagePageRef.current = data.page || messagePage;
      setPage(data.page || messagePage);
      setHasMore(Boolean(data.hasMore));
    } catch (error) {
      if (axios.isCancel(error) || requestId !== messageRequestIdRef.current) return;
      const statusCode = error?.response?.status;
      if (statusCode === 403 || statusCode === 404) {
        selectedTicketRef.current = null;
        setSelectedTicket(null);
        setMessages([]);
        setPage(1);
        setHasMore(false);
      }
      toastError(error);
    } finally {
      if (requestId === messageRequestIdRef.current) {
        loadingMessagePagesRef.current.delete(messagePage);
        messageRequestRef.current = null;
        setMessagesLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadTickets(1);
    const timer = setInterval(() => {
      loadTickets(1);
      const activeTicket = selectedTicketRef.current;
      if (activeTicket && messagePageRef.current === 1) loadMessages(activeTicket, 1);
    }, 15000);
    return () => {
      clearInterval(timer);
      loadingMessagePagesRef.current.clear();
      setMessagesLoading(false);
      ticketRequestIdRef.current += 1;
      messageRequestIdRef.current += 1;
      if (ticketRequestRef.current) ticketRequestRef.current.cancel("Management page unmounted or filters changed.");
      if (messageRequestRef.current) messageRequestRef.current.cancel("Management page unmounted or selection changed.");
    };
  }, [loadTickets, loadMessages]);

  if (!isManager && user?.profile === "admin") {
    return <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box px={3} pt={2}>
        <Typography variant="h5">Supervisão e gerência</Typography>
        <Typography color="textSecondary">Gerencie os responsáveis pela supervisão e suas filas e conexões.</Typography>
        <Tabs value={directoryTab} onChange={(_, value) => setDirectoryTab(value)} indicatorColor="primary" textColor="primary">
          <Tab value="managers" label="Gerentes da empresa" />
          {user.super && <Tab value="supervisors" label="Supervisores de empresas" />}
        </Tabs>
      </Box>
      {directoryTab === "supervisors" && user.super ? <Supervisors /> : <Users directory="management" />}
    </div>;
  }

  if (!isManager) {
    return (
      <MainContainer>
        <Box p={3}>
          <Typography variant="h5" gutterBottom>Sem permissão</Typography>
          <Typography color="textSecondary">Esta área é restrita a administradores e gerentes de supervisão.</Typography>
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Box p={{ xs: 1, md: 3 }} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} style={{ display: isMobile && selectedTicket ? "none" : "flex" }}>
          <Box display="flex" alignItems="center" gridGap={10}>
            <SupervisorAccountIcon color="primary" />
            <Typography variant="h5">Supervisão e gerência</Typography>
          </Box>
          <Button onClick={() => { loadTickets(1); if (selectedTicketRef.current) loadMessages(selectedTicketRef.current, 1); }} startIcon={<RefreshIcon />} disabled={loading}>
            Atualizar
          </Button>
        </Box>
        <Typography variant="body2" color="textSecondary" gutterBottom style={{ display: isMobile && selectedTicket ? "none" : "block" }}>
          Acompanhamento somente leitura. As conversas são limitadas às suas filas e conexões.
        </Typography>
        <Box display="flex" flexWrap="wrap" gridGap={8} mb={2} style={{ display: isMobile && selectedTicket ? "none" : "flex" }}>
          {(user.queues || []).map(queue => <React.Fragment key={`q${queue.id}`}>{metadataChip("Fila", queue.name, QueueIcon)}</React.Fragment>)}
          {(user.whatsapps || []).map(whatsapp => <React.Fragment key={`w${whatsapp.id}`}>{metadataChip("Conexão", whatsapp.name, WhatsAppIcon, "secondary")}</React.Fragment>)}
        </Box>
        <Grid container spacing={2} style={{ flex: 1, minHeight: 0, flexWrap: "nowrap", flexDirection: isMobile ? "column" : "row" }}>
          <Grid item xs={12} md={5} style={{ display: isMobile && selectedTicket ? "none" : "flex", flex: isMobile ? "1 1 0%" : undefined, minHeight: 0, flexDirection: "column" }}>
            <Card variant="outlined" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <CardContent style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                <TextField fullWidth size="small" label="Buscar nome ou telefone" value={searchInput} onChange={event => setSearchInput(event.target.value)} />
                <TextField fullWidth select size="small" label="Situação" value={status} onChange={event => setStatus(event.target.value)} style={{ marginTop: 12 }}>
                  {statuses.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
                <Box mt={2} style={{ overflowY: "auto", minHeight: 0, flex: 1 }}>
                  {loading && tickets.length === 0 ? <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box> : null}
                  {!loading && tickets.length === 0 ? <Typography color="textSecondary" align="center">Nenhuma conversa encontrada.</Typography> : null}
                  {ticketGroups.map(group => (
                    <Box key={group.value} mb={1} component="section" role="region" aria-label={group.label}>
                      <Typography variant="subtitle2" component="h2" color="textSecondary" gutterBottom>{group.label} ({group.tickets.length} {group.tickets.length === 1 ? "carregada" : "carregadas"})</Typography>
                      {group.tickets.map(ticket => (
                        <ButtonBase key={ticket.id} onClick={() => selectTicket(ticket)} aria-pressed={selectedTicket?.id === ticket.id} aria-label={`Abrir conversa com ${ticket.contact?.name || ticket.contact?.number || "Contato"}`} style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 8, borderRadius: 4 }}>
                          <Paper variant="outlined" style={{ padding: 12, borderColor: selectedTicket?.id === ticket.id ? theme.palette.primary.main : undefined, width: "100%" }}>
                            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gridGap={8}>
                              <Typography variant="subtitle2" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{ticket.contact?.name || ticket.contact?.number || "Contato"}</Typography>
                              <Chip size="small" label={statusLabel(ticket.status)} />
                            </Box>
                            <Box display="flex" flexWrap="wrap" gridGap={4} mt={1} mb={1}>
                              {metadataChip("Fila", ticket.queue?.name || "Fila", QueueIcon)}
                              {metadataChip("Usuário", ticket.user?.name || "Sem responsável", PersonIcon, "secondary")}
                              {metadataChip("Conexão", ticket.whatsapp?.name || "Conexão", WhatsAppIcon)}
                            </Box>
                            <Typography variant="body2" style={{ overflowWrap: "anywhere" }}>{ticket.lastMessage || "Conversa"}</Typography>
                          </Paper>
                        </ButtonBase>
                      ))}
                    </Box>
                  ))}
                  {ticketsHaveMore && <Box display="flex" justifyContent="center" mt={1}><Button onClick={() => loadTickets(ticketPage + 1, true)} disabled={loading}>Carregar mais conversas</Button></Box>}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7} style={{ display: isMobile && !selectedTicket ? "none" : "flex", flex: isMobile ? "1 1 0%" : undefined, minHeight: 0, flexDirection: "column" }}>
            <Card variant="outlined" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <CardContent style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
                {selectedTicket ? (
                  <>
                    {isMobile && <Button size="small" onClick={() => { if (messageRequestRef.current) messageRequestRef.current.cancel("Conversation closed."); selectedTicketRef.current = null; setSelectedTicket(null); }} style={{ alignSelf: "flex-start", minHeight: 44, marginBottom: 8 }}>Voltar às conversas</Button>}
                    <Typography variant="h6" style={{ overflowWrap: "anywhere" }}>{selectedTicket.contact?.name || selectedTicket.contact?.number}</Typography>
                    <Box display="flex" flexWrap="wrap" gridGap={6} mt={1}>
                      {metadataChip("Fila", selectedTicket.queue?.name || "Fila", QueueIcon)}
                      {metadataChip("Usuário", selectedTicket.user?.name || "Sem responsável", PersonIcon, "secondary")}
                      {metadataChip("Conexão", selectedTicket.whatsapp?.name || "Conexão", WhatsAppIcon)}
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                      <Button size="small" onClick={() => loadMessages(selectedTicket, 1)} disabled={messagesLoading} startIcon={<RefreshIcon />}>Atualizar conversa</Button>
                    </Box>
                    <Divider style={{ margin: "12px 0" }} />
                    {hasMore && <Button size="small" onClick={() => loadMessages(selectedTicket, page + 1)} disabled={messagesLoading}>Carregar mensagens anteriores</Button>}
                    {messagesLoading && <CircularProgress size={18} />}
                    <Box mt={1} style={{ overflowY: "auto", overflowX: "hidden", minHeight: 0, flex: 1 }}>
                      {messages.map(message => (
                        <Paper key={message.id} variant="outlined" style={{ padding: 10, margin: "8px 0", maxWidth: "90%", marginLeft: message.fromMe ? "auto" : 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          <ReadOnlyMessage message={message} />
                          <Typography variant="caption" color="textSecondary">{new Date(message.createdAt).toLocaleString()}</Typography>
                        </Paper>
                      ))}
                      {!messagesLoading && messages.length === 0 && <Typography color="textSecondary">Nenhuma mensagem disponível neste escopo.</Typography>}
                    </Box>
                  </>
                ) : <Typography color="textSecondary">Selecione uma conversa para acompanhar as mensagens.</Typography>}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MainContainer>
  );
};

export default Management;
